// AdminPanel/SearchTab — the admin panel's "Search" tab: reverse lookups
// that answer "which org does this belong to?".
//
// Built as a registry of search types so future lookups (phone number,
// message id, ...) plug in as new entries: add a component, append one row to
// SEARCH_TYPES, done.
//
// API Key needs its own endpoint (it matches a SHA256 hash no list column
// carries). Org ID, Owner Contact Email and Header do not: ListOrganizationsV3's
// `search` matches org id, name, contact name, the signup email/phone, and —
// through an EXISTS on sms_sender_ids — the header, all in one indexed query.
//
// "Owner Contact Email" is named for what it actually matches: the contact
// captured at SIGNUP, which is not necessarily the address the customer logs in
// with today. The Zitadel tab is the one that answers the login address.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Box, Tab, Tabs} from '@mui/material';

import {isSupportTierRole} from '../../auth/roles';
import {getFromLocalStorage} from '../../lib/utils';

import ApiKeyFinder from './ApiKeyFinder';
import OrgIdFinder from './OrgIdFinder';
import EmailFinder from './EmailFinder';
import HeaderFinder from './HeaderFinder';
import DltFinder from './DltFinder';
import ZitadelFinder from './ZitadelFinder';

// key: stable identifier (selector state), label: sub-tab caption,
// Render: component taking {orgs, onOpenOrg},
// adminOnly: hidden from the support tier.
//
// The support tier is deliberately narrower than what authz allows. authz
// permits platform_support on every finder here — including api_key — so this
// is a PRODUCT decision, not a security boundary, and the server would still
// answer if the tab were reachable. Two are held back:
//
//   API Key  a full customer API key is a live credential. Support does not
//            need to handle one to answer "who owns this account?".
//   Zitadel  queries identity directly and returns the user's CURRENT login
//            address, which is the account-recovery surface.
//
// Everything else — org id, owner contact email, header, DLT id — resolves a
// customer from something the customer already told support, which is the
// job.
const SEARCH_TYPES = [
  {key: 'apikey', label: 'API Key', Render: ApiKeyFinder, adminOnly: true},
  {key: 'orgid', label: 'Org ID', Render: OrgIdFinder},
  {key: 'email', label: 'Owner Contact Email', Render: EmailFinder},
  {key: 'header', label: 'Header', Render: HeaderFinder},
  {key: 'dlt', label: 'DLT ID', Render: DltFinder},
  {key: 'zitadel', label: 'Zitadel', Render: ZitadelFinder, adminOnly: true}
];

// Support sees the non-adminOnly subset. Computed per render rather than at
// module load: the role comes from localStorage, which is not populated when
// this module is first evaluated.
function visibleTypes() {
  const support = isSupportTierRole(getFromLocalStorage().role);
  return support ? SEARCH_TYPES.filter((t) => !t.adminOnly) : SEARCH_TYPES;
}

class SearchTab extends Component {
  state = {
    searchType: visibleTypes()[0].key
  };

  render() {
    const {orgs, onOpenOrg} = this.props;
    const {searchType} = this.state;
    const types = visibleTypes();
    // Falls back to the first VISIBLE tab, so a stale searchType from a role
    // change cannot render a hidden finder.
    const active = types.find((t) => t.key === searchType) || types[0];

    return (
      <div>
        <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
          <Tabs
            value={active.key}
            onChange={(e, v) => this.setState({searchType: v})}
          >
            {types.map((t) => (
              <Tab key={t.key} label={t.label} value={t.key} />
            ))}
          </Tabs>
        </Box>
        <active.Render orgs={orgs} onOpenOrg={onOpenOrg} />
      </div>
    );
  }
}

SearchTab.propTypes = {
  orgs: PropTypes.array,
  onOpenOrg: PropTypes.func.isRequired
};

export default SearchTab;
